// GVW-024 / Family G — compare shell chrome. Chooses two saved views as
// scenario pair and updates RFC-102 compare fields + time bus.
import { useState } from 'react';
import { Button, Callout, Card, Field, Stack, Text, tokens } from '@/design';
import { timeBus } from '@/state/timeBus';
import { useViewerStore } from '@/state/useViewerStore';

export function CompareScenariosPanel() {
  const savedViews = useViewerStore((s) => s.savedViews);
  const activeScenarioId = useViewerStore((s) => s.activeScenarioId);
  const compareWithScenarioId = useViewerStore((s) => s.compareWithScenarioId);
  const setCompareScenarios = useViewerStore((s) => s.setCompareScenarios);
  const clearCompare = useViewerStore((s) => s.clearCompare);
  const restoreSavedView = useViewerStore((s) => s.restoreSavedView);

  const [baseline, setBaseline] = useState(activeScenarioId ?? savedViews[0]?.id ?? '');
  const [compare, setCompare] = useState(
    compareWithScenarioId ?? (savedViews.length > 1 ? savedViews[1]!.id : savedViews[0]?.id ?? ''),
  );

  const canCompare = savedViews.length >= 2;

  const apply = () => {
    if (baseline && compare && baseline === compare) return;
    if (!canCompare || !baseline || !compare) return;
    setCompareScenarios(baseline, compare);
    restoreSavedView(baseline);
    timeBus.publish({ mode: 'compare', scenarioId: baseline });
  };

  const onClear = () => {
    clearCompare();
    timeBus.publish({ mode: 'live' });
  };

  if (!canCompare) {
    return (
      <Callout severity="info" title="Save two views first">
        <Text variant="body" as="p" style={{ margin: 0 }}>
          Compare needs at least two saved views. Use the layer panel to save the current view twice with different
          layer sets or time pins.
        </Text>
      </Callout>
    );
  }

  return (
    <Stack direction="column" gap={2} style={{ minWidth: 0 }}>
      <Text variant="body" as="p" style={{ margin: 0 }}>
        Select two saved views as baseline and comparison. The baseline view is applied to the map or 3D stage.
      </Text>

      <Field label="Baseline scenario">
        {({ id }) => (
        <select
          id={id}
          value={baseline}
          onChange={(e) => setBaseline(e.target.value)}
          aria-label="Baseline scenario"
          style={{
            width: '100%',
            minHeight: tokens.density.touchMin,
            borderRadius: tokens.radius.input,
            border: tokens.border.thin,
            background: tokens.color.surface[0],
            color: tokens.color.ink.primary,
            paddingInline: tokens.space[2],
            fontSize: tokens.font.size.md,
            fontFamily: tokens.font.family.ui,
          }}
        >
          {savedViews.map((v) => (
            <option key={v.id} value={v.id}>
              {v.name}
            </option>
          ))}
        </select>
        )}
      </Field>

      <Field label="Compare with scenario">
        {({ id }) => (
        <select
          id={id}
          value={compare}
          onChange={(e) => setCompare(e.target.value)}
          aria-label="Comparison scenario"
          style={{
            width: '100%',
            minHeight: tokens.density.touchMin,
            borderRadius: tokens.radius.input,
            border: tokens.border.thin,
            background: tokens.color.surface[0],
            color: tokens.color.ink.primary,
            paddingInline: tokens.space[2],
            fontSize: tokens.font.size.md,
            fontFamily: tokens.font.family.ui,
          }}
        >
          {savedViews.map((v) => (
            <option key={v.id} value={v.id}>
              {v.name}
            </option>
          ))}
        </select>
        )}
      </Field>

      <Stack direction="row" align="center" gap={1} style={{ flexWrap: 'wrap' }}>
        <Button
          data-testid="apply-compare"
          disabled={!baseline || !compare || baseline === compare}
          onClick={apply}
        >
          Apply compare
        </Button>
        <Button variant="secondary" onClick={onClear} type="button">
          Clear compare
        </Button>
      </Stack>

      {activeScenarioId && compareWithScenarioId ? (
        <Card
          level={1}
          header="Active pair"
        >
          <Text variant="caption" as="p" style={{ margin: 0 }}>
            Baseline {activeScenarioId}; compare {compareWithScenarioId}.
          </Text>
        </Card>
      ) : null}
    </Stack>
  );
}
